import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

/**
 * Filters that are kept in the address, so a filtered view can be bookmarked, shared and
 * reached with the back button.
 *
 * The values live in ordinary state and are copied to the address, not read from it on
 * every render: the router treats a change of address as a low-priority update, and a
 * text box that waits for it lags behind the typing.
 */
export function useFilters<T extends Record<string, string>>(defaults: T) {
  const [params, setParams] = useSearchParams();
  const defaultsRef = useRef(defaults);

  const read = useCallback(
    (from: URLSearchParams): T =>
      Object.fromEntries(
        Object.entries(defaultsRef.current).map(([name, fallback]) => [name, from.get(name) ?? fallback]),
      ) as T,
    [],
  );

  const [values, setValues] = useState<T>(() => read(params));

  // The addresses this hook wrote and that the router has yet to arrive at, oldest
  // first. While someone types, the router works through them one by one; each of those
  // arrivals is an echo of what was typed, not news. Only an address that is not in
  // this list came from outside (a link, the back button) and is followed.
  const pending = useRef<string[]>([params.toString()]);

  useEffect(() => {
    const current = params.toString();
    const position = pending.current.lastIndexOf(current);
    if (position >= 0) {
      pending.current = pending.current.slice(position);
      return;
    }
    pending.current = [current];
    setValues(read(params));
  }, [params, read]);

  const write = useCallback(
    (next: T) => {
      setValues(next);
      const address = new URLSearchParams(params);
      for (const [name, value] of Object.entries(next)) {
        if (value && value !== defaultsRef.current[name]) address.set(name, value);
        else address.delete(name);
      }
      pending.current = [...pending.current, address.toString()].slice(-100);
      setParams(address, { replace: true });
    },
    [params, setParams],
  );

  return {
    values,
    set: (name: keyof T, value: string) => write({ ...values, [name]: value }),
    /** Back to the defaults, except for the filters named in [keep]. */
    reset: (keep: (keyof T)[] = []) =>
      write({
        ...defaultsRef.current,
        ...Object.fromEntries(keep.map((name) => [name, values[name]])),
      }),
    /** True if any filter, other than those in [ignore], differs from its default. */
    isActive: (ignore: (keyof T)[] = []) =>
      Object.entries(values).some(
        ([name, value]) => !ignore.includes(name) && value !== defaultsRef.current[name],
      ),
  };
}
